import { useState, useEffect, useRef } from 'react';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaWhatsapp, FaClock, FaPaperPlane } from 'react-icons/fa';
import { contactInfo } from '../data/siteData';
import { sanitizeFormData, validateEmail, validatePhone, validateText, validateMessage } from '../utils/helpers';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [focused, setFocused] = useState({});
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Sanitize input on change
    const sanitizedValue = typeof value === 'string' ? value.trim() : value;
    setFormData({
      ...formData,
      [name]: sanitizedValue
    });
  };

  const handleFocus = (field) => {
    setFocused({ ...focused, [field]: true });
  };

  const handleBlur = (field) => {
    if (!formData[field]) {
      setFocused({ ...focused, [field]: false });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Security: Validate all fields
    const errors = {};
    
    if (!validateText(formData.name, { minLength: 2, maxLength: 100, required: true })) {
      errors.name = 'Please enter a valid name (2-100 characters)';
    }
    
    if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (formData.phone && !validatePhone(formData.phone)) {
      errors.phone = 'Please enter a valid phone number (7-15 digits)';
    }
    
    if (!validateText(formData.subject, { minLength: 3, maxLength: 200, required: true })) {
      errors.subject = 'Please enter a valid subject (3-200 characters)';
    }
    
    if (!validateMessage(formData.message, { minLength: 10, maxLength: 2000, required: true })) {
      errors.message = 'Please enter a valid message (10-2000 characters)';
    }
    
    // If there are validation errors, show alert and return
    if (Object.keys(errors).length > 0) {
      alert('Please fix the following errors:\n' + Object.values(errors).join('\n'));
      return;
    }
    
    // Sanitize all form data before processing
    const sanitizedData = sanitizeFormData(formData);
    console.log('Form submitted:', sanitizedData);
    
    // Create the WhatsApp message using sanitized data
    const message = `*Contact Us - New Message*

*Name:* ${sanitizedData.name}
*Email:* ${sanitizedData.email}
*Phone:* ${sanitizedData.phone || 'Not provided'}
*Subject:* ${sanitizedData.subject}

*Message:*
${sanitizedData.message}

Please respond to this inquiry.`;

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // Get WhatsApp number from contactInfo and remove all non-digits
    const whatsappNumber = contactInfo.whatsapp.replace(/[^0-9]/g, '');
    
    // Open WhatsApp with pre-filled message
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    
    alert('Thank you for contacting us! Opening WhatsApp to send your message...');
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
    setFocused({});
  };

  const telHref = `tel:${contactInfo.phone1.replace(/[^0-9+]/g, '')}`;
  const waHref = `https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`;

  const contactMethods = [
    {
      icon: FaPhone,
      title: 'Phone',
      items: [
        { label: contactInfo.phone1, href: telHref },
        { label: contactInfo.phone2, href: `tel:${contactInfo.phone2.replace(/[^0-9+]/g, '')}` }
      ],
      color: 'var(--islamic-green)'
    },
    {
      icon: FaWhatsapp,
      title: 'WhatsApp',
      items: [
        { label: contactInfo.whatsapp, href: waHref, external: true }
      ],
      color: '#25D366'
    },
    {
      icon: FaEnvelope,
      title: 'Email',
      items: [
        { label: 'Info@alnoortutors.com', href: 'mailto:Info@alnoortutors.com' }
      ],
      color: 'var(--golden)'
    }, 
    {
      icon: FaClock,
      title: 'Available',
      items: [
        { label: '24/7 Flexible Schedule', href: null }
      ],
      color: 'var(--islamic-green-dark)'
    }
  ];

  return (
    <div className="contact-page">
      {/* Hero Header */}
      <div className="contact-hero">
        <div className="contact-hero-overlay"></div>
        <div className="container">
          <div className="contact-hero-content">
            <span className="contact-badge">Get In Touch</span>
            <h1 className="contact-hero-title">Contact Us</h1>
            <p className="contact-hero-subtitle">
              We're here to help you on your Quran learning journey. Reach out to us anytime!
            </p>
          </div>
        </div>
      </div>

      {/* Main Contact Section */}
      <section ref={sectionRef} className={`contact-section ${isVisible ? 'visible' : ''}`}>
        <div className="container">
          <div className="contact-grid">
            {/* Contact Info Cards */}
            <div className="contact-info-wrapper">
              <div className="contact-info-header">
                <h2>Connect With Us</h2>
                <p>Choose your preferred method to reach out. We're available 24/7 to assist you.</p>
              </div>
              
              <div className="contact-cards-grid">
                {contactMethods.map((method, index) => {
                  const Icon = method.icon;
                  return (
                    <div
                      key={index}
                      className={`contact-card ${isVisible ? 'visible' : ''}`}
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="contact-card-icon" style={{ backgroundColor: `${method.color}15`, color: method.color }}>
                        <Icon />
                      </div>
                      <div className="contact-card-content">
                        <h3>{method.title}</h3>
                        <div className="contact-card-items">
                          {method.items.map((item, idx) => (
                            item.href ? (
                              <a
                                key={idx}
                                href={item.href}
                                target={item.external ? '_blank' : undefined}
                                rel={item.external ? 'noopener noreferrer' : undefined}
                                className="contact-card-link"
                              >
                                {item.label}
                              </a>
                            ) : (
                              <span key={idx} className="contact-card-text">{item.label}</span>
                            )
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <div className="contact-form-header">
                <div className="form-header-icon">
                  <FaPaperPlane />
                </div>
                <h2>Send Us a Message</h2>
                <p>Fill out the form below and we'll get back to you as soon as possible.</p>
              </div>

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onFocus={() => handleFocus('name')}
                    onBlur={() => handleBlur('name')}
                    required
                  />
                  <label className={focused.name || formData.name ? 'focused' : ''}>
                    Your Name <span className="required">*</span>
                  </label>
                </div>

                  <div className="form-group">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => handleFocus('email')}
                      onBlur={() => handleBlur('email')}
                      required
                    />
                    <label className={focused.email || formData.email ? 'focused' : ''}>
                      Your Email <span className="required">*</span>
                    </label>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onFocus={() => handleFocus('phone')}
                      onBlur={() => handleBlur('phone')}
                    />
                    <label className={focused.phone || formData.phone ? 'focused' : ''}>
                      Your Phone
                    </label>
                  </div>

                  <div className="form-group">
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      onFocus={() => handleFocus('subject')}
                      onBlur={() => handleBlur('subject')}
                      required
                    />
                    <label className={focused.subject || formData.subject ? 'focused' : ''}>
                      Subject <span className="required">*</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => handleFocus('message')}
                    onBlur={() => handleBlur('message')}
                    rows="6"
                    required
                  ></textarea>
                  <label className={focused.message || formData.message ? 'focused' : ''}>
                    Your Message <span className="required">*</span>
                  </label>
                </div>

                <button type="submit" className="contact-submit-btn">
                  <span>Send Message</span>
                  <FaPaperPlane className="btn-icon" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
